import React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { ChevronDown } from 'lucide-react';

/**
 * Reusable multi-select filter (popover + checkbox list).
 *
 * Empty selection = "all" (no filter). Props:
 *   options   array of strings OR {value, label}
 *   selected  array of selected values
 *   onChange  (newSelectedArray) => void
 *   placeholder  shown when nothing is selected (e.g. "All Branches")
 *   widthClass   tailwind width for the trigger (default w-48)
 */
export function MultiSelect({ options = [], selected = [], onChange, placeholder = 'All', widthClass = 'w-48', className = '', testId }) {
  const opts = (options || []).map((o) => (typeof o === 'object' ? o : { value: o, label: String(o) }));

  const toggle = (v) => {
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  };

  const label =
    selected.length === 0
      ? placeholder
      : selected.length === 1
      ? (opts.find((o) => o.value === selected[0])?.label ?? String(selected[0]))
      : `${selected.length} selected`;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className={`${widthClass} justify-between font-normal ${className}`} data-testid={testId}>
          <span className="truncate text-sm">{label}</span>
          <ChevronDown className="w-4 h-4 opacity-50 shrink-0 ml-1" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-1 max-h-72 overflow-y-auto" align="start">
        {selected.length > 0 && (
          <button
            type="button"
            className="w-full text-left text-xs text-[#C85A40] px-2 py-1.5 hover:bg-[#F5F5FA] rounded"
            onClick={() => onChange([])}
          >
            Clear ({selected.length})
          </button>
        )}
        {opts.length === 0 ? (
          <div className="px-2 py-1.5 text-xs text-[#8A8E8B]">No options</div>
        ) : (
          opts.map((o) => (
            <label
              key={o.value}
              className="flex items-center gap-2 px-2 py-1.5 hover:bg-[#F5F5FA] rounded cursor-pointer text-sm"
            >
              <Checkbox checked={selected.includes(o.value)} onCheckedChange={() => toggle(o.value)} />
              <span className="truncate">{o.label}</span>
            </label>
          ))
        )}
      </PopoverContent>
    </Popover>
  );
}

export default MultiSelect;
